import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { auth, db } from "../../firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { collection, getDocs, query, where, limit, orderBy, addDoc, serverTimestamp } from "firebase/firestore";
import { httpsCallable, getFunctions } from "firebase/functions";
import { Icons } from "../../lib/Icons";
import { SensiFloatingWidget } from "../../components/sensi";
import SoloAuthModal from "../../components/solo/SoloAuthModal";

const isSoloLanding = process.env.REACT_APP_SOLO_LANDING === 'true';

// Domain configuration with colors and icons (no patterns - using lighter gradient backgrounds)
const DOMAINS = [
  {
    id: 'Communication',
    label: 'Communication',
    Icon: Icons.ChatBubble,
    color: 'from-blue-400/80 to-cyan-400/40',
    bgColor: 'bg-blue-500/10',
    iconColor: 'text-blue-400'
  },
  {
    id: 'SEMH',
    label: 'Social & Emotional',
    Icon: Icons.Heart,
    color: 'from-green-400/80 to-emerald-400/40',
    bgColor: 'bg-green-500/10',
    iconColor: 'text-green-400'
  },
  {
    id: 'Cognition',
    label: 'Cognition & Learning',
    Icon: Icons.Brain,
    color: 'from-purple-400/80 to-violet-400/40',
    bgColor: 'bg-purple-500/10',
    iconColor: 'text-purple-400'
  },
  {
    id: 'Sensory/Physical',
    label: 'Sensory & Physical',
    Icon: Icons.Hand,
    color: 'from-orange-400/80 to-amber-400/40',
    bgColor: 'bg-orange-500/10',
    iconColor: 'text-orange-400'
  },
];

// Age group configuration with icon components
const AGE_GROUPS = [
  { id: 'eyfs', label: 'EYFS', ageMin: 0, ageMax: 4, Icon: Icons.Star, description: 'Ages 0-4' },
  { id: 'primary', label: 'Primary', ageMin: 4, ageMax: 11, Icon: Icons.School, description: 'Ages 4-11' },
  { id: 'secondary', label: 'Secondary', ageMin: 11, ageMax: 16, Icon: Icons.AcademicCap, description: 'Ages 11-16', hasSubOptions: true },
  { id: 'all', label: 'All Ages', ageMin: 0, ageMax: 19, Icon: Icons.BookOpen, description: 'All resources' },
];

// Key stage sub-options for secondary
const KEY_STAGE_OPTIONS = [
  { id: 'ks3', label: 'Key Stage 3', description: 'Years 7-9', ageMin: 11, ageMax: 14, keyStage: 'ks3' },
  { id: 'ks4', label: 'Key Stage 4', description: 'Years 10-11', ageMin: 14, ageMax: 16, keyStage: 'ks4' },
];

// Feature tools configuration - NEW features should be at the top with isNew: true
const FEATURE_TOOLS = [
  {
    id: 'resources',
    name: 'Resources',
    description: 'Teaching resources, activities, and downloadable packs',
    path: '/solo/resources',
    Icon: Icons.FolderOpen,
    gradient: 'from-rose-500 to-orange-500',
    hoverGradient: 'hover:from-rose-600 hover:to-orange-600',
    isNew: true,
  },
  {
    id: 'assessments',
    name: 'Assessments & Screeners',
    description: 'SDQ, SNAP-IV & dyslexia screening tools',
    path: '/solo/assessments',
    Icon: Icons.ClipboardDocumentCheck,
    gradient: 'from-rose-500 to-pink-500',
    hoverGradient: 'hover:from-rose-600 hover:to-pink-600',
    isNew: true,
  },
  {
    id: 'advice-hub',
    name: 'Advice Hub',
    description: 'In-depth SEND articles & guidance',
    path: '/solo/articles',
    Icon: Icons.BookOpen,
    gradient: 'from-violet-500 to-purple-500',
    hoverGradient: 'hover:from-violet-600 hover:to-purple-600',
    isNew: true,
  },
  {
    id: 'learning-profiles',
    name: 'Learning Profiles',
    description: 'Build a profile to get personalized recommendations',
    path: '/solo/profiles',
    Icon: Icons.ClipboardDocumentList,
    gradient: 'from-cyan-500 to-blue-500',
    hoverGradient: 'hover:from-cyan-600 hover:to-blue-600',
    isNew: true,
  },
  {
    id: 'resource-builder',
    name: 'Resource Builder',
    description: 'Create visual schedules, social stories & more',
    path: '/solo/resources',
    Icon: Icons.Document,
    gradient: 'from-emerald-500 to-teal-500',
    hoverGradient: 'hover:from-emerald-600 hover:to-teal-600',
    isNew: true,
  },
  {
    id: 'interventions',
    name: 'Interventions',
    description: 'Browse evidence-based SEN interventions',
    path: '/solo/search?interventions=true',
    Icon: Icons.Target,
    gradient: 'from-amber-500 to-orange-500',
    hoverGradient: 'hover:from-amber-600 hover:to-orange-600',
    isNew: true,
  },
  {
    id: 'symbol-generator',
    name: 'Symbol Generator',
    description: 'Generate AAC symbols for any word',
    path: '/solo/symbols',
    Icon: Icons.Sparkles,
    gradient: 'from-indigo-500 to-purple-500',
    hoverGradient: 'hover:from-indigo-600 hover:to-purple-600',
    isNew: false,
  },
];

// Helper function to get domain styling for activity tiles
const getDomainStyle = (domainId) => {
  const domain = DOMAINS.find(d => d.id === domainId);
  if (!domain) {
    return {
      gradient: 'from-slate-600/60 to-slate-700/30',
      iconColor: 'text-slate-400',
      Icon: Icons.Puzzle
    };
  }
  return {
    gradient: domain.color,
    iconColor: domain.iconColor,
    Icon: domain.Icon
  };
};

const RE_OFFER_GET_STARTED_UNTIL_LOGIN_COUNT = 3;

const SoloDashboard = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activities, setActivities] = useState([]);
  const [featuredActivities, setFeaturedActivities] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [getStartedCompleted, setGetStartedCompleted] = useState(false);
  const [soloLoginCount, setSoloLoginCount] = useState(0);
  const [dismissedGetStartedThisSession, setDismissedGetStartedThisSession] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [selectedAgeGroup, setSelectedAgeGroup] = useState('all');
  const [selectedKeyStage, setSelectedKeyStage] = useState(null);
  const [showInterventionsOnly, setShowInterventionsOnly] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [feedbackRequest, setFeedbackRequest] = useState("");
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const navigate = useNavigate();
  const activitiesSectionRef = useRef(null);
  const sensiWidgetRef = useRef(null);

  // Scroll to activities section
  const scrollToActivities = () => {
    if (activitiesSectionRef.current) {
      activitiesSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);

      // Check subscription only if user is authenticated
      if (currentUser) {
        try {
          const functions = getFunctions(undefined, 'europe-west3');
          const checkSub = httpsCallable(functions, 'checkSoloSubscriptionFunc');
          const result = await checkSub();

          // Redirect school users to the school portal - they can't use solo site
          if (result.data.status === 'not_solo_user') {
            alert('This account is associated with a school. Redirecting to school portal.');
            navigate('/');
            return;
          }

          setSubscriptionStatus(result.data);
          setGetStartedCompleted(result.data.getStartedCompleted === true);
          setSoloLoginCount(result.data.soloLoginCount ?? 0);
          // Increment login count on dashboard load (for re-offer logic)
          const updateGetStarted = httpsCallable(functions, 'updateSoloGetStartedFunc');
          updateGetStarted({ incrementLoginCount: true }).catch(() => {});
          // No redirect - let users browse freely, paywall is on activity detail
        } catch (e) {
          console.error('Subscription check failed:', e);
          // Set default status for new users
          setSubscriptionStatus({ hasActiveSubscription: false, status: 'none' });
        }
      } else {
        // Unauthenticated users - set subscription status to null to indicate guest state
        setSubscriptionStatus(null);
        setGetStartedCompleted(false);
        setSoloLoginCount(0);
      }

      // Load activities (available to all users, authenticated or not)
      await loadActivities();
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  const loadActivities = async () => {
    try {
      const kbRef = collection(db, 'kb_items');
      const snapshot = await getDocs(kbRef);

      const allActivities = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        allActivities.push({
          id: doc.id,
          ...data,
        });
      });

      setActivities(allActivities);

      // Set featured (random selection of 12)
      const shuffled = [...allActivities].sort(() => Math.random() - 0.5);
      setFeaturedActivities(shuffled.slice(0, 12));
    } catch (error) {
      console.error('Error loading activities:', error);
    }
  };


  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/solo/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/");
  };

  const handleManageSubscription = async () => {
    try {
      const functions = getFunctions(undefined, 'europe-west3');
      const createPortal = httpsCallable(functions, 'createCustomerPortalFunc');
      const result = await createPortal();
      window.location.href = result.data.url;
    } catch (e) {
      console.error('Error creating portal:', e);
      alert('Unable to open subscription management. Please try again.');
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    if (!feedbackRequest.trim() || feedbackSubmitting) return;

    setFeedbackSubmitting(true);
    try {
      await addDoc(collection(db, 'feedback_requests'), {
        userId: user?.uid || null,
        email: user?.email || '',
        request: feedbackRequest.trim(),
        status: 'pending',
        createdAt: serverTimestamp(),
      });

      setFeedbackSubmitted(true);
      setFeedbackRequest("");

      // Reset success message after 5 seconds
      setTimeout(() => {
        setFeedbackSubmitted(false);
      }, 5000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const getFilteredActivities = () => {
    let filtered = activities;

    // Browse: hide umbrella cards (they only appear on Interventions tab)
    if (!showInterventionsOnly) {
      filtered = filtered.filter(a => !a.interventionUmbrella);
    }

    // Filter by domain
    if (selectedDomain) {
      filtered = filtered.filter(a =>
        a.targetDomains?.includes(selectedDomain)
      );
    }

    // Filter by age group
    if (selectedAgeGroup && selectedAgeGroup !== 'all') {
      const ageGroup = AGE_GROUPS.find(g => g.id === selectedAgeGroup);
      if (ageGroup) {
        filtered = filtered.filter(a => {
          // Check phases array first (new format supports multiple phases)
          if (a.phases && Array.isArray(a.phases)) {
            return a.phases.includes(selectedAgeGroup);
          }

          // Backward compatibility: check legacy phase field
          if (a.phase) {
            if (a.phase === 'both') {
              // 'both' means primary and eyfs (legacy behavior)
              return selectedAgeGroup === 'primary' || selectedAgeGroup === 'eyfs';
            }
            return a.phase === selectedAgeGroup;
          }

          // Fallback: use age range to determine eligibility
          const minAge = a.ageRange?.min || 0;
          const maxAge = a.ageRange?.max || 19;

          if (selectedAgeGroup === 'eyfs') {
            return minAge <= 4 && maxAge >= 0; // Overlaps with EYFS range (0-4)
          } else if (selectedAgeGroup === 'primary') {
            return minAge <= 11 && maxAge >= 4; // Overlaps with Primary range (4-11)
          } else if (selectedAgeGroup === 'secondary') {
            return minAge <= 16 && maxAge >= 11; // Overlaps with Secondary range (11-16)
          }

          return true;
        });
      }
    }

    // Filter by key stage (for secondary sub-options)
    if (selectedKeyStage && selectedAgeGroup === 'secondary') {
      const ksOption = KEY_STAGE_OPTIONS.find(ks => ks.id === selectedKeyStage);
      if (ksOption) {
        filtered = filtered.filter(a => {
          // Check if activity has keyStages field
          if (a.keyStages && Array.isArray(a.keyStages)) {
            return a.keyStages.includes(ksOption.keyStage);
          }
          // Fallback to age range
          const minAge = a.ageRange?.min || 0;
          const maxAge = a.ageRange?.max || 19;
          return minAge <= ksOption.ageMax && maxAge >= ksOption.ageMin;
        });
      }
    }

    // Filter by interventions only: show umbrella + other interventions, hide child stages
    if (showInterventionsOnly) {
      filtered = filtered.filter(a => a.isIntervention === true && !a.parentInterventionSlug);
    }

    return filtered;
  };

  const filteredActivities = getFilteredActivities();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500/20 border-t-purple-500 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading resources...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Slide-out Navigation Menu */}
      <div className={`fixed inset-0 z-[60] transition-opacity duration-300 ${menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={() => setMenuOpen(false)}
        />

        {/* Menu Panel */}
        <div className={`absolute left-0 top-0 h-full w-72 bg-slate-800 shadow-2xl transform transition-transform duration-300 ${menuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          {/* Menu Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-700">
            <div className="flex items-center gap-3">
              <img
                src="/brand/logo-thumbnail-purple.png"
                alt="SEN-Education"
                className="w-10 h-10 rounded-xl"
              />
              <span className="text-lg font-bold text-white">SEN-Education</span>
            </div>
            <button
              onClick={() => setMenuOpen(false)}
              className="p-2 rounded-lg hover:bg-slate-700 transition-colors"
            >
              <Icons.XMark className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          {/* Menu Content */}
          <div className="p-4 space-y-6 overflow-y-auto h-[calc(100%-80px)]">
            {/* Tools Section */}
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Tools</h3>
              <div className="space-y-1">
                <Link
                  to="/solo/articles"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-700 transition-colors group relative"
                >
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
                    <Icons.BookOpen className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-slate-200 group-hover:text-white">Advice Hub</span>
                  <span className="absolute right-3 px-2 py-0.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs font-bold rounded-full">NEW</span>
                </Link>

                <Link
                  to="/solo/profiles"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-700 transition-colors group relative"
                >
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
                    <Icons.ClipboardDocumentList className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-slate-200 group-hover:text-white">Learning Profiles</span>
                  <span className="absolute right-3 px-2 py-0.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs font-bold rounded-full">NEW</span>
                </Link>

                <Link
                  to="/solo/symbols"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-700 transition-colors group"
                >
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                    <Icons.Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-slate-200 group-hover:text-white">Symbol Generator</span>
                </Link>

                <Link
                  to="/solo/resources"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-700 transition-colors group relative"
                >
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                    <Icons.Document className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-slate-200 group-hover:text-white">Resource Builder</span>
                  <span className="absolute right-3 px-2 py-0.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs font-bold rounded-full">NEW</span>
                </Link>

                <Link
                  to="/solo/search?interventions=true"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-700 transition-colors group relative"
                >
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                    <Icons.Target className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-slate-200 group-hover:text-white">Interventions</span>
                  <span className="absolute right-3 px-2 py-0.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs font-bold rounded-full">NEW</span>
                </Link>

                <Link
                  to="/solo/resources"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-700 transition-colors group relative"
                >
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center">
                    <Icons.FolderOpen className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-slate-200 group-hover:text-white">Resources</span>
                  <span className="absolute right-3 px-2 py-0.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs font-bold rounded-full">NEW</span>
                </Link>
              </div>
            </div>

            {/* Browse Section */}
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Browse</h3>
              <div className="space-y-1">
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    setSelectedAgeGroup('all');
                    setSelectedDomain(null);
                    setShowInterventionsOnly(false);
                    setTimeout(scrollToActivities, 100);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-700 transition-colors group text-left"
                >
                  <div className="w-9 h-9 rounded-lg bg-slate-700 flex items-center justify-center">
                    <Icons.BookOpen className="w-5 h-5 text-slate-300" />
                  </div>
                  <span className="text-slate-200 group-hover:text-white">All Activities</span>
                </button>

                <button
                  onClick={() => {
                    setMenuOpen(false);
                    setSelectedAgeGroup('all');
                    setSelectedDomain(null);
                    setShowInterventionsOnly(true);
                    setTimeout(scrollToActivities, 100);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-700 transition-colors group text-left relative"
                >
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                    <Icons.Target className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-slate-200 group-hover:text-white">Interventions</span>
                  <span className="absolute right-3 px-2 py-0.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs font-bold rounded-full">NEW</span>
                </button>

                <button
                  onClick={() => { setMenuOpen(false); setTimeout(scrollToActivities, 100); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-700 transition-colors group text-left"
                >
                  <div className="w-9 h-9 rounded-lg bg-slate-700 flex items-center justify-center">
                    <Icons.Puzzle className="w-5 h-5 text-slate-300" />
                  </div>
                  <span className="text-slate-200 group-hover:text-white">By Domain</span>
                </button>

                <button
                  onClick={() => { setMenuOpen(false); setTimeout(scrollToActivities, 100); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-700 transition-colors group text-left"
                >
                  <div className="w-9 h-9 rounded-lg bg-slate-700 flex items-center justify-center">
                    <Icons.AcademicCap className="w-5 h-5 text-slate-300" />
                  </div>
                  <span className="text-slate-200 group-hover:text-white">By Age Group</span>
                </button>
              </div>
            </div>

            {/* Account Section - Only show for authenticated users */}
            {user && (
              <div>
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Account</h3>
                <div className="space-y-1">
                  <button
                    onClick={() => { setMenuOpen(false); navigate('/solo/account'); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-700 transition-colors group text-left"
                  >
                    <div className="w-9 h-9 rounded-lg bg-slate-700 flex items-center justify-center">
                      <Icons.Cog className="w-5 h-5 text-slate-300" />
                    </div>
                    <span className="text-slate-200 group-hover:text-white">Settings</span>
                  </button>

                  {subscriptionStatus?.hasActiveSubscription ? (
                    <button
                      onClick={() => { setMenuOpen(false); handleManageSubscription(); }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-700 transition-colors group text-left"
                    >
                      <div className="w-9 h-9 rounded-lg bg-slate-700 flex items-center justify-center">
                        <Icons.Cog className="w-5 h-5 text-slate-300" />
                      </div>
                      <span className="text-slate-200 group-hover:text-white">Manage Subscription</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => { setMenuOpen(false); navigate('/solo/checkout'); }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-700 transition-colors group text-left"
                    >
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                        <Icons.Star className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-slate-200 group-hover:text-white">Start Free Trial</span>
                    </button>
                  )}

                  <button
                    onClick={() => { setMenuOpen(false); handleLogout(); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-700 transition-colors group text-left"
                  >
                    <div className="w-9 h-9 rounded-lg bg-slate-700 flex items-center justify-center">
                      <Icons.ArrowRight className="w-5 h-5 text-slate-300 rotate-180" />
                    </div>
                    <span className="text-slate-200 group-hover:text-white">Sign Out</span>
                  </button>
                </div>
              </div>
            )}

            {/* Site links */}
            <div className="pt-4 border-t border-slate-700">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Site</h3>
              <div className="space-y-1">
                <Link to="/about" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-700 transition-colors text-slate-200 hover:text-white">
                  About
                </Link>
                <Link to="/contact" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-700 transition-colors text-slate-200 hover:text-white">
                  Contact
                </Link>
              </div>
            </div>

            {/* School Portal Section */}
            <div className="pt-4 border-t border-slate-700">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">For Schools</h3>
              <div className="space-y-1">
                {isSoloLanding ? (
                  <a
                    href="https://schools.sen-ed.co.uk"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-700 transition-colors group"
                  >
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-blue-500 flex items-center justify-center">
                      <Icons.School className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <span className="text-slate-200 group-hover:text-white block">School Portal</span>
                      <span className="text-xs text-slate-500">For SENCOs & school staff</span>
                    </div>
                  </a>
                ) : (
                  <Link
                    to="/login"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-700 transition-colors group"
                  >
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-blue-500 flex items-center justify-center">
                      <Icons.School className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <span className="text-slate-200 group-hover:text-white block">School Portal</span>
                      <span className="text-xs text-slate-500">For SENCOs & school staff</span>
                    </div>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/80 backdrop-blur-lg border-b border-slate-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              {/* Hamburger Menu Button */}
              <button
                onClick={() => setMenuOpen(true)}
                className="p-2 rounded-lg hover:bg-slate-700 transition-colors"
                aria-label="Open menu"
              >
                <Icons.Bars3 className="w-6 h-6 text-white" />
              </button>
              <img
                src="/brand/logo-thumbnail-purple.png"
                alt="SEN-Education"
                className="w-10 h-10 rounded-xl"
              />
              <span className="text-xl font-bold text-white">SEN-Education</span>
            </div>

            <div className="flex items-center gap-4">
              {/* Header Search Bar */}
              <form
                onSubmit={handleSearch}
                className="hidden sm:flex items-center"
              >
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search resources..."
                    className="w-48 md:w-64 lg:w-80 px-4 py-2 pl-10 bg-slate-800/80 text-white placeholder-slate-400 rounded-xl border border-slate-700 focus:border-purple-500 focus:ring-1 focus:ring-purple-500/20 focus:outline-none text-sm transition-all"
                  />
                  <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                    >
                      <Icons.XMark className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </form>

              {user && subscriptionStatus?.status === 'trialing' && (
                <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 bg-green-500/10 text-green-400 rounded-full text-sm">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                  Free Trial
                </span>
              )}

              {/* Show log in button for unauthenticated users, subscription management for authenticated */}
              {!user ? (
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-medium rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all"
                >
                  Log In
                </button>
              ) : subscriptionStatus !== null && (
                subscriptionStatus?.hasActiveSubscription ? (
                  <button
                    onClick={handleManageSubscription}
                    className="text-sm text-slate-400 hover:text-white transition-colors"
                  >
                    Manage Subscription
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/solo/checkout')}
                    className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-medium rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all"
                  >
                    Start Free Trial
                  </button>
                )
              )}

              {user && (
                <div className="relative group">
                  <button className="w-9 h-9 bg-slate-700 rounded-full flex items-center justify-center text-white hover:bg-slate-600 transition-colors">
                    {user?.displayName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || '?'}
                  </button>
                  <div className="absolute right-0 mt-2 w-48 bg-slate-800 rounded-xl shadow-xl border border-slate-700 py-2 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all">
                    <div className="px-4 py-2 border-b border-slate-700">
                      <p className="text-sm text-white font-medium truncate">{user?.email}</p>
                    </div>
                    <button
                      onClick={() => navigate('/solo/account')}
                      className="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white"
                    >
                      Account Settings
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white"
                    >
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Get started strip – shown when logged in and get-started not completed */}
      {user && !getStartedCompleted && (
        <div className="bg-violet-900/30 border-b border-violet-700/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center justify-center gap-3">
            <p className="text-slate-200 text-sm">
              New here? Let Sensi help you get set up and point you to the right resources.
            </p>
            <button
              type="button"
              onClick={() => sensiWidgetRef.current?.open?.()}
              className="px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-600 text-white text-sm font-medium rounded-xl hover:from-violet-600 hover:to-purple-700 transition-all"
            >
              Get started with Sensi
            </button>
            <button
              type="button"
              onClick={() => setDismissedGetStartedThisSession(true)}
              className="text-slate-500 hover:text-slate-400 text-sm"
            >
              Skip
            </button>
          </div>
        </div>
      )}

      {/* Hero Section with Search */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/50 via-slate-900 to-pink-900/30"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOGM5Ljk0MSAwIDE4LTguMDU5IDE4LTE4cy04LjA1OS0xOC0xOC0xOHptMCAzMmMtNy43MzIgMC0xNC02LjI2OC0xNC0xNHM2LjI2OC0xNCAxNC0xNCAxNCA2LjI2OCAxNCAxNC02LjI2OCAxNC0xNCAxNHoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjAyIi8+PC9nPjwvc3ZnPg==')] opacity-50"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Find the Perfect SEN
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 text-transparent bg-clip-text"> Teaching Resource</span>
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              Search through hundreds of evidence-based activities, strategies, and resources for Special Educational Needs
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Try 'autism year 2' or 'communication EYFS'..."
                  className="w-full px-6 py-4 pl-14 bg-slate-800/80 backdrop-blur-sm text-white placeholder-slate-400 rounded-2xl border border-slate-700 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none text-lg"
                />
                <svg className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <button
                  type="submit"
                  className="absolute right-3 top-1/2 -translate-y-1/2 px-5 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all"
                >
                  Search
                </button>
              </div>
            </form>

            {/* Quick search suggestions */}
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {['PECS', 'Sensory', 'Makaton', 'Attention Autism', 'Zones of Regulation'].map(term => (
                <button
                  key={term}
                  onClick={() => navigate(`/solo/search?q=${encodeURIComponent(term)}`)}
                  className="px-3 py-1.5 text-sm text-slate-300 bg-slate-800/50 hover:bg-slate-700 rounded-full transition-colors"
                >
                  {term}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quick Actions - Feature Tools */}
      <section className="py-6 bg-gradient-to-r from-slate-800/80 to-slate-900/80 border-b border-slate-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {FEATURE_TOOLS.map(tool => {
              const ToolIcon = tool.Icon;
              return (
                <Link
                  key={tool.id}
                  to={tool.path}
                  className={`relative flex items-center gap-3 px-6 py-4 bg-gradient-to-r ${tool.gradient} ${tool.hoverGradient} text-white font-medium rounded-2xl transition-all shadow-lg hover:shadow-xl hover:scale-105 group`}
                >
                  {/* NEW Badge */}
                  {tool.isNew && (
                    <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs font-bold rounded-full shadow-lg animate-pulse">
                      NEW
                    </span>
                  )}
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <ToolIcon className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold">{tool.name}</p>
                    <p className="text-xs text-white/70">{tool.description}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Age Group Selector */}
      <section className="py-8 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {AGE_GROUPS.map(group => {
              const GroupIcon = group.Icon;
              return (
                <button
                  key={group.id}
                  onClick={() => {
                    setSelectedAgeGroup(group.id);
                    // Clear key stage when switching age groups
                    if (group.id !== 'secondary') {
                      setSelectedKeyStage(null);
                    }
                    // Scroll to activities after a brief delay for state update
                    setTimeout(scrollToActivities, 100);
                  }}
                  className={`flex items-center gap-3 px-6 py-3 rounded-xl transition-all ${selectedAgeGroup === group.id
                    ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/25'
                    : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    }`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${selectedAgeGroup === group.id
                    ? 'bg-white/20'
                    : 'bg-purple-500/20'
                    }`}>
                    <GroupIcon className={`w-5 h-5 ${selectedAgeGroup === group.id ? 'text-white' : 'text-purple-400'
                      }`} />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold">{group.label}</p>
                    <p className="text-xs opacity-75">{group.description}</p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Key Stage sub-options for Secondary */}
          {selectedAgeGroup === 'secondary' && (
            <div className="mt-4 flex justify-center gap-3">
              <span className="text-slate-400 text-sm self-center mr-2">Filter by:</span>
              {KEY_STAGE_OPTIONS.map(ks => (
                <button
                  key={ks.id}
                  onClick={() => {
                    setSelectedKeyStage(selectedKeyStage === ks.id ? null : ks.id);
                    setTimeout(scrollToActivities, 100);
                  }}
                  className={`px-4 py-2 rounded-lg text-sm transition-all ${selectedKeyStage === ks.id
                    ? 'bg-pink-500 text-white shadow-lg shadow-pink-500/25'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                >
                  <span className="font-medium">{ks.label}</span>
                  <span className="ml-1 opacity-75">({ks.description})</span>
                </button>
              ))}
              {selectedKeyStage && (
                <button
                  onClick={() => setSelectedKeyStage(null)}
                  className="px-3 py-2 text-sm text-slate-400 hover:text-white transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Domain Cards */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white mb-6">Browse by Domain</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {DOMAINS.map(domain => {
              const count = activities.filter(a => a.targetDomains?.includes(domain.id)).length;
              const isSelected = selectedDomain === domain.id;
              const DomainIcon = domain.Icon;

              return (
                <button
                  key={domain.id}
                  onClick={() => {
                    setSelectedDomain(isSelected ? null : domain.id);
                    if (!isSelected) {
                      setTimeout(scrollToActivities, 100);
                    }
                  }}
                  className={`relative p-6 rounded-2xl transition-all group ${isSelected
                    ? `bg-gradient-to-br ${domain.color} shadow-lg`
                    : 'bg-slate-800 hover:bg-slate-750'
                    }`}
                >
                  <div className={`w-14 h-14 rounded-xl mb-3 flex items-center justify-center transition-all ${isSelected
                    ? 'bg-white/20'
                    : `${domain.bgColor} group-hover:scale-110`
                    }`}>
                    <DomainIcon className={`w-7 h-7 ${isSelected ? 'text-white' : domain.iconColor}`} />
                  </div>
                  <h3 className={`font-semibold mb-1 ${isSelected ? 'text-white' : 'text-slate-200'}`}>
                    {domain.label}
                  </h3>
                  <p className={`text-sm ${isSelected ? 'text-white/80' : 'text-slate-400'}`}>
                    {count} resources
                  </p>
                  {isSelected && (
                    <div className="absolute top-3 right-3">
                      <Icons.Check className="w-5 h-5 text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured/Filtered Activities */}
      <section ref={activitiesSectionRef} className="py-12 bg-slate-800/30 scroll-mt-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white">
              {showInterventionsOnly
                ? 'Interventions'
                : selectedDomain && selectedAgeGroup !== 'all'
                  ? `${DOMAINS.find(d => d.id === selectedDomain)?.label} - ${AGE_GROUPS.find(g => g.id === selectedAgeGroup)?.label}${selectedKeyStage ? ` (${KEY_STAGE_OPTIONS.find(k => k.id === selectedKeyStage)?.label})` : ''}`
                  : selectedDomain
                    ? `${DOMAINS.find(d => d.id === selectedDomain)?.label} Resources`
                    : selectedAgeGroup !== 'all'
                      ? `${AGE_GROUPS.find(g => g.id === selectedAgeGroup)?.label} Resources${selectedKeyStage ? ` - ${KEY_STAGE_OPTIONS.find(k => k.id === selectedKeyStage)?.label}` : ''}`
                      : 'Featured Resources'}
            </h2>
            {(selectedDomain || selectedAgeGroup !== 'all' || selectedKeyStage || showInterventionsOnly) && (
              <div className="flex items-center gap-2">
                {showInterventionsOnly && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-500/20 text-amber-400 rounded-full text-sm">
                    <Icons.Target className="w-3 h-3" />
                    Interventions
                  </span>
                )}
                <button
                  onClick={() => {
                    setSelectedDomain(null);
                    setSelectedAgeGroup('all');
                    setSelectedKeyStage(null);
                    setShowInterventionsOnly(false);
                  }}
                  className="text-sm text-purple-400 hover:text-purple-300"
                >
                  Clear filters ×
                </button>
              </div>
            )}
          </div>

          {/* Activity Grid */}
          {((selectedDomain || selectedAgeGroup !== 'all' || selectedKeyStage || showInterventionsOnly) ? filteredActivities : featuredActivities).length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-slate-700/50 flex items-center justify-center">
                <Icons.Search className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">No resources found</h3>
              <p className="text-slate-400 mb-4">Try adjusting your filters or browse all resources</p>
              <button
                onClick={() => {
                  setSelectedDomain(null);
                  setSelectedAgeGroup('all');
                  setSelectedKeyStage(null);
                  setShowInterventionsOnly(false);
                }}
                className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-xl transition-colors"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {((selectedDomain || selectedAgeGroup !== 'all' || selectedKeyStage || showInterventionsOnly)
                ? filteredActivities
                : featuredActivities.slice(0, 12)
              ).map(activity => {
                const primaryDomain = activity.targetDomains?.[0];
                const domainStyle = getDomainStyle(primaryDomain);
                const TileIcon = domainStyle.Icon;

                return (
                  <Link
                    key={activity.id}
                    to={`/solo/activity/${activity.id}`}
                    className="group bg-slate-800 rounded-2xl overflow-hidden hover:ring-2 hover:ring-purple-500/50 transition-all"
                  >
                    {/* Activity thumbnail/header with domain color gradient */}
                    <div
                      className={`h-32 bg-gradient-to-br ${domainStyle.gradient} p-4 flex items-end relative overflow-hidden`}
                    >
                      {/* Domain icon in corner */}
                      <div className="absolute top-3 right-3 w-10 h-10 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center">
                        <TileIcon className={`w-5 h-5 ${domainStyle.iconColor}`} />
                      </div>

                      <div className="flex gap-2 flex-wrap">
                        {activity.targetDomains?.slice(0, 2).map(domain => {
                          const domainInfo = DOMAINS.find(d => d.id === domain);
                          return (
                            <span
                              key={domain}
                              className="px-2 py-1 bg-white/10 backdrop-blur-sm rounded-full text-xs text-white flex items-center gap-1"
                            >
                              {domainInfo && <domainInfo.Icon className="w-3 h-3" />}
                              {domain}
                            </span>
                          );
                        })}
                      </div>
                    </div>

                    <div className="p-5">
                      <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-purple-400 transition-colors">
                        {activity.title}
                      </h3>
                      <p className="text-slate-400 text-sm line-clamp-2 mb-4">
                        {activity.description}
                      </p>

                      {/* Meta info */}
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        {activity.keyStages?.length > 0 ? (
                          <span>{activity.keyStages.map(ks => ks.toUpperCase()).join(' / ')}</span>
                        ) : activity.ageRange ? (
                          <span>Ages {activity.ageRange.min}-{activity.ageRange.max}</span>
                        ) : null}
                        {activity.recommendedFrequency && (
                          <span>{activity.recommendedFrequency.sessionMinutes} min</span>
                        )}
                        {activity.resources && (
                          <span>{Array.isArray(activity.resources) ? activity.resources.length : '0'} resources</span>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {/* View all button */}
          <div className="text-center mt-8 space-y-4">
            <Link
              to={`/solo/search${selectedDomain || selectedAgeGroup !== 'all' || selectedKeyStage
                ? `?${selectedDomain ? `domain=${selectedDomain}` : ''}${selectedDomain && selectedAgeGroup !== 'all' ? '&' : ''}${selectedAgeGroup !== 'all' ? `age=${selectedAgeGroup}` : ''}${selectedKeyStage ? `${selectedDomain || selectedAgeGroup !== 'all' ? '&' : ''}ks=${selectedKeyStage}` : ''}`
                : ''}`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition-colors"
            >
              View All {(selectedDomain || selectedAgeGroup !== 'all' || selectedKeyStage) ? 'Filtered' : ''} Resources
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>

            {/* Show more option when filters are active */}
            {(selectedDomain || selectedAgeGroup !== 'all' || selectedKeyStage || showInterventionsOnly) && (
              <div className="pt-2">
                <p className="text-slate-400 text-sm mb-2">Want to see more activities?</p>
                <button
                  onClick={() => {
                    setSelectedDomain(null);
                    setSelectedAgeGroup('all');
                    setSelectedKeyStage(null);
                    setShowInterventionsOnly(false);
                  }}
                  className="text-purple-400 hover:text-purple-300 text-sm font-medium transition-colors"
                >
                  View all {activities.length} activities →
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Symbol Generator Feature Card */}
      <section className="py-8 bg-gradient-to-r from-indigo-900/50 to-purple-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-800/50 rounded-2xl p-8 border border-indigo-500/30 flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0 w-20 h-20 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-2xl flex items-center justify-center">
              <Icons.Sparkles className="w-10 h-10 text-white" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-2xl font-bold text-white mb-2">
                AI Symbol Generator
              </h3>
              <p className="text-slate-300 mb-4">
                Create custom AAC communication symbols instantly. Type any word and get a beautifully designed symbol for visual schedules, communication boards, and more.
              </p>
              <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                {['hello', 'eat', 'happy', 'school'].map(word => (
                  <span key={word} className="px-3 py-1 bg-slate-700/50 text-slate-300 rounded-full text-sm">
                    {word}
                  </span>
                ))}
                <span className="px-3 py-1 text-slate-400 text-sm">+ any word</span>
              </div>
            </div>
            <button
              onClick={() => navigate('/solo/symbols')}
              className="flex-shrink-0 px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-medium rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all flex items-center gap-2"
            >
              <Icons.Sparkles className="w-5 h-5" />
              Try Symbol Generator
            </button>
          </div>
        </div>
      </section>

      {/* Resource Builder Feature Card */}
      <section className="py-8 bg-gradient-to-r from-emerald-900/50 to-teal-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-800/50 rounded-2xl p-8 border border-emerald-500/30 flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0 w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center">
              <Icons.Document className="w-10 h-10 text-white" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-2xl font-bold text-white mb-2">
                Resource Builder
              </h3>
              <p className="text-slate-300 mb-4">
                Create printable visual schedules, social stories, choice boards, reward charts, and more. All with professional ARASAAC symbols.
              </p>
              <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                <span className="px-3 py-1 bg-emerald-500/20 text-emerald-300 rounded-full text-sm">Visual Schedules</span>
                <span className="px-3 py-1 bg-teal-500/20 text-teal-300 rounded-full text-sm">Social Stories</span>
                <span className="px-3 py-1 bg-cyan-500/20 text-cyan-300 rounded-full text-sm">Choice Boards</span>
                <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">Task Analysis</span>
              </div>
            </div>
            <button
              onClick={() => navigate('/solo/resources')}
              className="flex-shrink-0 px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-medium rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all flex items-center gap-2"
            >
              <Icons.Document className="w-5 h-5" />
              Build Resources
            </button>
          </div>
        </div>
      </section>


      {/* Stats Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: activities.length, label: 'Teaching Activities', Icon: Icons.BookOpen, color: 'text-purple-400', bgColor: 'bg-purple-500/20' },
              { value: activities.reduce((acc, a) => acc + (Array.isArray(a.resources) ? a.resources.length : 0), 0), label: 'Video Resources', Icon: Icons.Film, color: 'text-pink-400', bgColor: 'bg-pink-500/20' },
              { value: DOMAINS.length, label: 'SEN Domains', Icon: Icons.Target, color: 'text-blue-400', bgColor: 'bg-blue-500/20' },
              { value: '15+', label: 'Evidence-Based Strategies', Icon: Icons.CheckCircle, color: 'text-green-400', bgColor: 'bg-green-500/20' },
            ].map((stat, idx) => {
              const StatIcon = stat.Icon;
              return (
                <div key={idx} className="text-center p-6 bg-slate-800/50 rounded-2xl">
                  <div className={`w-14 h-14 mx-auto mb-3 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                    <StatIcon className={`w-7 h-7 ${stat.color}`} />
                  </div>
                  <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                  <div className="text-sm text-slate-400">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Feedback Request Box */}
      <section className="py-12 bg-slate-800/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-800/80 backdrop-blur-lg rounded-2xl border border-purple-500/30 p-8">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-white mb-2">
                Can't find what you're looking for?
              </h3>
              <p className="text-slate-300">
                Let us know and we will create it.
              </p>
            </div>

            <form onSubmit={handleFeedbackSubmit} className="space-y-4">
              <textarea
                value={feedbackRequest}
                onChange={(e) => setFeedbackRequest(e.target.value)}
                placeholder="Describe the resource or feature you'd like to see..."
                rows={4}
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                disabled={feedbackSubmitting || feedbackSubmitted}
              />

              {feedbackSubmitted ? (
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-300 rounded-full">
                    <Icons.Check className="w-5 h-5" />
                    <span className="font-medium">Thank you! We'll review your request.</span>
                  </div>
                </div>
              ) : (
                <div className="flex justify-center">
                  <button
                    type="submit"
                    disabled={!feedbackRequest.trim() || feedbackSubmitting}
                    className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {feedbackSubmitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Icons.PaperAirplane className="w-4 h-4" />
                        Submit Request
                      </>
                    )}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-slate-400">
              <img
                src="/brand/logo-thumbnail-purple.png"
                alt="SEN-Education"
                className="w-8 h-8 rounded-lg"
              />
              <span className="font-medium text-white">SEN-Education</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-400">
              {!user ? (
                <button onClick={() => setShowAuthModal(true)} className="hover:text-white transition-colors">
                  Log In
                </button>
              ) : subscriptionStatus !== null && (
                subscriptionStatus?.hasActiveSubscription ? (
                  <button onClick={handleManageSubscription} className="hover:text-white transition-colors">
                    Manage Subscription
                  </button>
                ) : (
                  <button onClick={() => navigate('/solo/checkout')} className="hover:text-white transition-colors">
                    Start Free Trial
                  </button>
                )
              )}
              {isSoloLanding ? (
                <a href="https://schools.sen-ed.co.uk" rel="noopener noreferrer" className="hover:text-white transition-colors">
                  School Login
                </a>
              ) : (
                <button onClick={() => navigate('/login')} className="hover:text-white transition-colors">
                  School Login
                </button>
              )}
              <a href="mailto:support@sen-ed.co.uk" className="hover:text-white transition-colors">
                Contact Support
              </a>
            </div>
          </div>
          {/* Legal Links */}
          <div className="mt-6 pt-6 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
            <p>© {new Date().getFullYear()} SEN-Education. All rights reserved.</p>
            <div className="flex flex-wrap items-center justify-center sm:justify-end gap-4 sm:gap-6">
              <Link to="/about" className="hover:text-slate-300 transition-colors">About</Link>
              <Link to="/contact" className="hover:text-slate-300 transition-colors">Contact</Link>
              <Link to="/privacy" className="hover:text-slate-300 transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-slate-300 transition-colors">Terms</Link>
              <Link to="/security" className="hover:text-slate-300 transition-colors">Security</Link>
              <Link to="/cookies" className="hover:text-slate-300 transition-colors">Cookies</Link>
              <a href="https://sen-ed.co.uk" rel="noopener noreferrer" className="hover:text-slate-300 transition-colors">Go to our schools site</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Auth modal: log in with option to create account */}
      <SoloAuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />

      {/* Sensi AI Assistant floating widget; initiates get-started on first few logins if not completed */}
      <SensiFloatingWidget
        ref={sensiWidgetRef}
        shouldInitiate={
          !!user &&
          !getStartedCompleted &&
          soloLoginCount <= RE_OFFER_GET_STARTED_UNTIL_LOGIN_COUNT &&
          !dismissedGetStartedThisSession
        }
        mode={getStartedCompleted ? 'normal' : 'get_started'}
        onGetStartedComplete={() => setGetStartedCompleted(true)}
        onDismissGetStarted={() => setDismissedGetStartedThisSession(true)}
      />
    </div>
  );
};

export default SoloDashboard;


